'use client';

import { Menu as MUIMenu, MenuProps as MUIMenuProps, MenuItem, MenuItemProps } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { cn } from '../../lib';

export interface TMenuItem {
  title: string;
  onClick: (data?: any) => void;
}

export interface MenuHandles {
  open: (event: React.MouseEvent<HTMLButtonElement>) => void;
  close: () => void;
}

export interface MenuProps extends Omit<MUIMenuProps, 'open' | 'onClose' | 'anchorEl' | 'children' | 'className'> {
  className?: string;
  menuPaperStyle?: any;
  menuItemChildren?: React.ReactNode;
  menuItemProps?: MenuItemProps;
  data?: TMenuItem[] | TMenuItem;
  bgColor?: string;
  onOpen?: (data?: any) => void;
  onClose?: (data?: any) => void;
  onChangeOpen?: (isOpen: boolean) => void;
}

export const Menu = forwardRef<MenuHandles, MenuProps>(
  (
    {
      sx,
      data,
      className,
      menuPaperStyle,
      menuItemProps,
      menuItemChildren,
      bgColor = 'white',
      onOpen,
      onClose,
      onChangeOpen,
      ...rest
    },
    ref,
  ) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    useEffect(() => {
      if (onChangeOpen) {
        onChangeOpen(open);
      }
    }, [open, onChangeOpen]);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
      onOpen?.();
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      onClose?.();
      setAnchorEl(null);
    };

    useImperativeHandle(ref, () => ({ open: handleOpen, close: handleClose }), [handleOpen, handleClose]);

    const onMenuItemClick = (item: TMenuItem) => () => {
      item.onClick();
      handleClose();
    };

    const renderMenuItems = () => {
      if (!data) return null;

      if (Array.isArray(data)) {
        return data.map((item, index) => (
          <MenuItem {...menuItemProps} key={`${index}`} onClick={onMenuItemClick(item)}>
            {item.title}
          </MenuItem>
        ));
      }

      return (
        <MenuItem {...menuItemProps} onClick={onMenuItemClick(data)}>
          {data.title}
        </MenuItem>
      );
    };

    return (
      <MUIMenu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        className={cn(className)}
        sx={{
          '& .MuiMenu-paper': {
            minWidth: '15.25rem',
            backgroundColor: bgColor,
            ...menuPaperStyle,
          },
          ...sx,
        }}
        {...rest}>
        {menuItemChildren ?? renderMenuItems()}
      </MUIMenu>
    );
  },
);

Menu.displayName = 'Menu';
